//@flow
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import * as Immutable from 'immutable';
import TerritoryCreationTypeSelector from './automatic/steps/TerritoryCreationTypeSelector';
import OutletSelection, {
    type SelectedOutletsTypes
} from './automatic/steps/OutletSelection';
import TerritoryConfig from './automatic/steps/TerritoryConfig';
import { CreateTerritoryConfig } from 'common/models/internal/CreateTerritoryConfig';
import { type PlanConfigConstantsModelType } from 'common/models/flow/CreateTerritoryConfig';
import FilterListBuilder from 'common/models/helpers/FilterListBuilder';
import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';
import { Loader } from 'common/components/core/loader';
import { TerritoryAddEditContext } from './TerritoryAddEditProvider';

const DEFAULT_ACTIVE_FILTER = [
    new FilterBuilder()
        .withName('status')
        .withOperation(FilterOperation.Equals)
        .withValue('ACTIVE')
];

const STEPS_ENUM = Object.freeze([
    { type: 'SELECT_APPROACH' },
    { type: 'SELECT_OUTLETS' },
    { type: 'SELECT_PARAMETERS' }
]);

type CommonReturnType = {
    activeStep: { type: string },
    onDismiss: () => void
};

const FORM_STATE_ACTION_TYPES = Object.freeze({
    UPDATE_NAME: 'UPDATE_NAME',
    UPDATE_CONFIG: 'UPDATE_CONFIG',
    UPDATE_OUTLETS_CONFIG: 'UPDATE_OUTLETS_CONFIG'
});

const initialFormState = CreateTerritoryConfig.fromJS({
    clientId: '',
    name: '',
    configuration: {
        revenueFairness: false,
        tatFairness: true,
        outletNumberFairness: true,
        minTerritory: 2,
        maxTerritory: 5
    },
    outletsData: {
        ids: []
    }
});

const territoryFormStateReducer = (
    state: CreateTerritoryConfig,
    action: {
        type: $Values<typeof FORM_STATE_ACTION_TYPES>,
        payload: Object,
        ...
    }
) => {
    switch (action.type) {
        case FORM_STATE_ACTION_TYPES.UPDATE_CONFIG: {
            const {
                payload: {
                    value: { config, name },
                    updatedStateCallback
                }
            } = action;
            const newState = state
                .setName(name)
                .setConfiguration(
                    state.configuration
                        .setRevenueFairness(config.revenueFairness)
                        .setTatFairness(config.tatFairness)
                        .setOutletNumberFairness(config.outletNumberFairness)
                        .setMinTerritory(config.minTerritory)
                        .setMaxTerritory(config.maxTerritory)
                );
            updatedStateCallback(newState);
            return newState;
        }
        case FORM_STATE_ACTION_TYPES.UPDATE_OUTLETS_CONFIG: {
            const {
                payload: { value }
            } = action;
            return state.setOutletsData(value);
        }
        default:
            throw new Error(
                `Received ${
                    action.type
                } is invalid action type, valid type are ${Object.values(
                    FORM_STATE_ACTION_TYPES
                ).join(', ')}`
            );
    }
};

const useTerritoryFormState = ({
    clientId
}): ({
    config: CreateTerritoryConfig,
    updateConstant: (config: {
        config: PlanConfigConstantsModelType,
        name: string
    }) => void,
    updateOutletConfig: (config: {
        allApplicableFilters: FilterListBuilder,
        allFilteredEntitiesSelected: boolean,
        selectedRows: ['string']
    }) => void
}) => {
    const [state, dispatch] = React.useReducer(
        territoryFormStateReducer,
        initialFormState,
        intiState => intiState.setClientId(clientId)
    );

    const updateConstant = React.useCallback((config, updatedStateCallback) => {
        dispatch({
            type: FORM_STATE_ACTION_TYPES.UPDATE_CONFIG,
            payload: {
                value: config,
                updatedStateCallback
            }
        });
    }, []);

    const updateOutletConfig = React.useCallback(
        ({
            selectedRows,
            allApplicableFilters,
            allFilteredEntitiesSelected
        }) => {
            let outletsData;
            if (allFilteredEntitiesSelected) {
                outletsData = state.outletsData.setRequest(
                    state.outletsData.request.setFilters(
                        Immutable.List(
                            allApplicableFilters.addAll(DEFAULT_ACTIVE_FILTER)
                                .list
                        )
                    )
                );
            } else {
                outletsData = state.outletsData.setIds(
                    Immutable.List(selectedRows)
                );
            }
            dispatch({
                type: FORM_STATE_ACTION_TYPES.UPDATE_OUTLETS_CONFIG,
                payload: {
                    value: outletsData
                }
            });
        },
        [state.outletsData]
    );

    return {
        config: state,
        updateConstant,
        updateOutletConfig
    };
};

const _useTerritorySteps = ({
    clientId,
    onSubmit,
    showManualEditPlan
}): (
    | {
          ...CommonReturnType,
          onNext: (selectedTerritoryType: string) => void
      }
    | {
          ...CommonReturnType,
          onNext: (selectedOutletIds: SelectedOutletsTypes) => void,
          onPrev: () => void
      }
    | {
          ...CommonReturnType,
          onNext: (config: $FixMe) => void,
          onPrev: () => void
      }) => {
    const history = useHistory();
    const [activeStep, setActiveStep] = React.useState(STEPS_ENUM[0]);
    const activeStepIndex = React.useMemo(
        () => STEPS_ENUM.findIndex(step => step.type === activeStep.type),
        [activeStep]
    );

    const { updateConstant, updateOutletConfig } = useTerritoryFormState({
        clientId
    });

    const onNextStep = React.useCallback(() => {
        setActiveStep(STEPS_ENUM[activeStepIndex + 1]);
    }, [activeStepIndex]);

    const onPrevStep: () => void = React.useCallback(() => {
        setActiveStep(STEPS_ENUM[activeStepIndex - 1]);
    }, [activeStepIndex]);

    const onDismiss = React.useCallback(() => {
        history.replace(history.location.pathname);
    }, [history]);

    const onTerritoryTypeSelect = React.useCallback(
        selectedTerritoryType => {
            if (selectedTerritoryType === 'AUTOMATIC') {
                onNextStep();
            } else {
                showManualEditPlan();
            }
        },
        [onNextStep, showManualEditPlan]
    );

    const { disableNavigation } = React.useContext(TerritoryAddEditContext);

    const onNextSelectedOutlets: (
        outletIds: SelectedOutletsTypes
    ) => void = React.useCallback(
        selectedOutletIds => {
            disableNavigation();
            updateOutletConfig(selectedOutletIds);
            onNextStep();
        },
        [disableNavigation, onNextStep, updateOutletConfig]
    );

    const onNextConfig = React.useCallback(
        territoryConfig => {
            updateConstant(territoryConfig, onSubmit);
        },
        [onSubmit, updateConstant]
    );

    switch (activeStep.type) {
        case 'SELECT_APPROACH': {
            return { onNext: onTerritoryTypeSelect, activeStep, onDismiss };
        }
        case 'SELECT_OUTLETS':
            return {
                onPrev: onPrevStep,
                onNext: onNextSelectedOutlets,
                activeStep,
                onDismiss
            };
        case 'SELECT_PARAMETERS':
            return {
                onPrev: onPrevStep,
                onNext: onNextConfig,
                activeStep,
                onDismiss
            };
    }
};

const TerritoryStepsContainer = (props: {
    clientId: string,
    onSubmit: (config: CreateTerritoryConfig) => void,
    postingData: boolean,
    apiError: string,
    showManualEditPlan: () => void
}): React.Node => {
    const { activeStep, ...currentStepProps } = _useTerritorySteps({
        clientId: props.clientId,
        onSubmit: props.onSubmit,
        showManualEditPlan: props.showManualEditPlan
    });
    const StepComponent = () => {
        if (activeStep.type === 'SELECT_APPROACH') {
            return <TerritoryCreationTypeSelector {...currentStepProps} />;
        }
        if (activeStep.type === 'SELECT_OUTLETS') {
            return <OutletSelection {...currentStepProps} />;
        }
        if (activeStep.type === 'SELECT_PARAMETERS') {
            return <TerritoryConfig {...currentStepProps} />;
        }
        return <></>;
    };

    return (
        <>
            <Loader active={props.postingData} style={{ left: 0 }} />
            <StepComponent />
        </>
    );
};

export default TerritoryStepsContainer;
